<template>
  <v-dialog
    v-model="show"
    max-width="750px"
  >
    <v-card>
      <v-card-title>
        Request For Sport Compensation
        <v-spacer></v-spacer>
        <v-btn
          icon
          @click.prevent="show = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
        </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="py-2">
        <v-expansion-panels v-model="firstPanel">
          <v-expansion-panel>
            <v-expansion-panel-header class="ml-0">Attached files</v-expansion-panel-header>
            <v-expansion-panel-content>
              Please upload the confirmation of the payment.
              For season ticket it can be the contract, or other official documents with your name on it.
              Also, the document should have the date, the price (UAH), and vendor details like name, stamp, sign, etc.
              Don't forget to attach payment check.
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
        <v-row class="mb-2">
          <v-col cols="12" lg="6" md="6">
              <v-file-input
                label="Season ticket"
                show-size
                counter
                :accept="ALLOWED_FILE_TYPES"
                v-model="seasonTicketFile.file"
                @change="onSeasonTicketFileChange"
              >
              </v-file-input>
          </v-col>
          <v-col cols="12" lg="6" md="6">
            <v-file-input
                label="Payment check"
                show-size
                counter
                v-model="paymentCheckFile.file"
                @change="onPaymentCheckFileChange"
                :accept="ALLOWED_FILE_TYPES"
                :rules="[filesHaveDifferentNames||'Please, don\'t send 2 identical files']"
              >
              </v-file-input>
          </v-col>
        </v-row>
        <v-expansion-panels v-model="secondPanel">
          <v-expansion-panel>
            <v-expansion-panel-header class="ml-0">Other fields</v-expansion-panel-header>
            <v-expansion-panel-content>
              Fill in the start and the end date of your ticket period as well as the
              compensation amount.
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
        <v-row>
          <!-- Date From -->
          <v-col>
            <DatePicker
              v-model="computedFromDate"
              label="Start Date"
              :min="firstDayOfCurrentMonth"
            />
          </v-col>
          <!-- /Date From -->
          <!-- Date To -->
          <v-col>
            <DatePicker
              v-model="toDate"
              label="End Date"
              :min="lastDayOfCurrentMonth"
            />
          <!-- /Date To -->
          </v-col>
          <v-col cols="12" md="4" lg="4">
            <v-text-field
              v-model="computedCompensationAmount"
              class="inputAmount"
              label="Compensation amount"
              type="number"
              hint="Enter the full amount you paid"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn small :disabled="!isRequestReady() || !isRequestAppropriate" @click="sendRequest" color="primary">Send</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment'
import { ALLOWED_FILE_TYPES } from '@/constants/fileAccept'
import { getApiErrorMessage } from '@/utils/apiError'
import { formatLocalDate } from '@/utils/dateUtils'
import { fileToDataUrl } from '@/utils/fileDataUrl'

export default {
  name: 'SportCompensationRequest',
  components: {
    DatePicker: () => import('@/components/global/DatePicker.vue')
  },
  props: {
    value: {
      required: true,
      default: false
    }
  },
  data () {
    return {
      isRequestAppropriate: true,
      firstPanel: 0,
      secondPanel: 0,
      ALLOWED_FILE_TYPES,
      seasonTicketFile: {
        file: null,
        File_Name: '',
        File_Type: ''
      },
      paymentCheckFile: {
        file: null,
        File_Name: '',
        File_Type: ''
      },
      compensationAmount: null,
      fromDate: undefined,
      toDate: undefined,
      firstDayOfCurrentMonth: moment().startOf('month').format('YYYY-MM-DD'),
      lastDayOfCurrentMonth: moment().endOf('month').format('YYYY-MM-DD')
    }
  },
  computed: {
    show: {
      get () {
        return this.value
      },
      set (value) {
        this.$emit('input', value)
        if (this.value) {
          this.clearAll()
          this.isRequestAppropriate = true
        }
      }
    },
    user () {
      return this.$store.getters.getUser
    },
    computedCompensationAmount: {
      get () {
        return this.compensationAmount
      },
      set (val) {
        if (val < 0) {
          this.compensationAmount = -val
        } else {
          this.compensationAmount = val
        }
      }
    },
    computedFromDate: {
      get () {
        return this.fromDate
      },
      set (val) {
        this.fromDate = val
      }
    },
    uploadClass: {
      get () {
        const width = window.innerWidth || document.documentElement.clientWidth ||
        document.body.clientWidth
        if (width < 600) {
          return 'mt-0'
        }
        return 'mt-4'
      }
    },
    filesHaveDifferentNames () {
      if (!this.seasonTicketFile.File_Name || !this.paymentCheckFile.File_Name) {
        return true
      }
      return this.seasonTicketFile.File_Name !== this.paymentCheckFile.File_Name
    }
  },
  methods: {
    sendRequest () {
      this.isRequestAppropriate = false
      this.axios.post('/api/requests/sport_compensation',
        {
          Creator_ID: this.user.employee_id,
          Season_Ticket_File: this.seasonTicketFile,
          Payment_Check_File: this.paymentCheckFile,
          Period_From: formatLocalDate(this.fromDate),
          Period_To: formatLocalDate(this.toDate),
          Amount_Of_Compensation: this.compensationAmount
        },
        {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(() => {
        this.show = false
        this.$store.dispatch('setSuccess', { successMsg: 'Request for sport compensation was made' })
      }).catch(error => {
        this.isRequestAppropriate = true
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    deltaDate (input, days, months, years) {
      return new Date(
        input.getFullYear() + years,
        input.getMonth() + months,
        Math.min(
          input.getDate() + days,
          new Date(input.getFullYear() + years, input.getMonth() + months + 1, 0).getDate()
        )
      )
    },
    uploadFile (elementId) {
      document.getElementById(elementId).click()
    },
    onSeasonTicketFileChange (e) {
      this.onFileChange(e, this.seasonTicketFile)
    },
    onPaymentCheckFileChange (e) {
      this.onFileChange(e, this.paymentCheckFile)
    },
    async onFileChange (e, file) {
      if (!e) {
        file.file = null
        file.File_Name = ''
        file.File_Type = ''
        return
      }
      try {
        const res = await fileToDataUrl(e)
        file.File_Name = e.name
        file.File_Type = e.type
        if (file.file) {
          file.file.data = res.data
          file.file.base64String = res.base64String
        }
      } catch (error) {
        file.file = null
        file.File_Name = ''
        file.File_Type = ''
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      }
    },
    isRequestReady () {
      return this.seasonTicketFile.file &&
        this.paymentCheckFile.file &&
        this.fromDate &&
        this.toDate &&
        this.compensationAmount
    },
    clearAll () {
      this.seasonTicketFile = {
        file: null,
        File_Name: '',
        File_Type: ''
      }
      this.paymentCheckFile = {
        file: null,
        File_Name: '',
        File_Type: ''
      }
      this.compensationAmount = null
      this.fromDate = undefined
      this.toDate = undefined
    }
  }
}
</script>
<style>
.inputAmount input[type='number'] {
    -moz-appearance:textfield;
}
.inputAmount input::-webkit-outer-spin-button,
.inputAmount input::-webkit-inner-spin-button {
    -webkit-appearance: none;
}
</style>
